/**
 * <collection-subheader-recipients>
 * Dedicated JS component for recipient collection subheader tabs + scroll navigation.
 *
 * Default active tab on page load (checked in this order):
 *   1. The tab whose collection matches the current page URL
 *      (e.g. /collections/gifts-for-mum -> "Gifts for Mum").
 *      The grid already shows that collection, so nothing extra is loaded.
 *   2. A ?tab=<collection-handle> parameter in the URL
 *      (e.g. ?tab=gifts-for-mum). That tab is activated and its products load.
 *   3. Otherwise the FIRST tab is activated and its products load, so the
 *      active tab and the product grid always match.
 */
class CollectionSubheaderRecipients extends HTMLElement {
  constructor() {
    super();
    this.sectionId = this.dataset.sectionId;
    this.onClick = this.onClick.bind(this);
  }

  connectedCallback() {
    this.addEventListener('click', this.onClick);
    this.initScrollButtons();
    this.initActiveTab();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClick);
  }

  getTabs() {
    return Array.from(this.querySelectorAll('[data-subheader-recipients-tab]'));
  }

  // Collection handle from a URL, e.g. "/en-gb/collections/gifts-for-mum?x=1" -> "gifts-for-mum".
  // Comparing handles (not full URLs) ignores market/locale prefixes, query strings,
  // trailing slashes and letter case.
  getHandleFromUrl(url) {
    try {
      const path = new URL(url, window.location.origin).pathname;
      const match = path.match(/\/collections\/([^/?#]+)/i);
      return match ? decodeURIComponent(match[1]).toLowerCase() : null;
    } catch (error) {
      return null;
    }
  }

  setActiveTab(activeTab) {
    this.getTabs().forEach((tabEl) => {
      const isActive = tabEl === activeTab;
      tabEl.classList.toggle('is-active', isActive);
      tabEl.setAttribute('aria-selected', String(isActive));
    });
    this.scrollTabIntoView(activeTab);
  }

  // Keep the active tab visible in the scrollable tab row (mobile)
  scrollTabIntoView(tab) {
    const tabsNav = this.querySelector('.collection-subheader-recipients__tabs');
    if (!tabsNav || !tab) return;

    requestAnimationFrame(() => {
      const navRect = tabsNav.getBoundingClientRect();
      const tabRect = tab.getBoundingClientRect();
      const isHiddenLeft = tabRect.left < navRect.left;
      const isHiddenRight = tabRect.right > navRect.right;

      if (isHiddenLeft || isHiddenRight) {
        const offset = tabRect.left - navRect.left + tabsNav.scrollLeft - 16;
        tabsNav.scrollTo({ left: Math.max(offset, 0), behavior: 'auto' });
      }
    });
  }

  initActiveTab() {
    const tabs = this.getTabs();
    if (!tabs.length) return;

    const pageHandle = this.getHandleFromUrl(window.location.href);

    // 1. Current page is one of the tab collections
    const urlMatch = tabs.find((tab) => this.getHandleFromUrl(tab.dataset.tabUrl) === pageHandle);
    if (urlMatch) {
      this.setActiveTab(urlMatch);
      return;
    }

    // 2. ?tab=<handle> in the URL
    const tabParam = new URLSearchParams(window.location.search).get('tab');
    if (tabParam) {
      const paramHandle = tabParam.toLowerCase();
      const paramMatch = tabs.find((tab) => this.getHandleFromUrl(tab.dataset.tabUrl) === paramHandle);
      if (paramMatch) {
        this.setActiveTab(paramMatch);
        this.loadCollection(paramMatch.dataset.tabUrl, { history: 'replace' });
        return;
      }
    }

    // 3. Default: first tab, and load its products so the grid matches
    const firstTab = tabs[0];
    this.setActiveTab(firstTab);
    this.loadCollection(firstTab.dataset.tabUrl, { history: 'replace' });
  }

  initScrollButtons() {
    const tabsNav = this.querySelector('.collection-subheader-recipients__tabs');
    const prevBtn = this.querySelector('[data-subheader-recipients-tabs-prev]');
    const nextBtn = this.querySelector('[data-subheader-recipients-tabs-next]');

    if (!tabsNav || (!prevBtn && !nextBtn)) return;

    const scrollAmount = () => Math.min(tabsNav.clientWidth * 0.6, 240);

    const updateButtons = () => {
      const maxScrollLeft = tabsNav.scrollWidth - tabsNav.clientWidth;
      const scrollLeft = tabsNav.scrollLeft;
      if (prevBtn) prevBtn.disabled = scrollLeft <= 4;
      if (nextBtn) nextBtn.disabled = scrollLeft >= maxScrollLeft - 4;
    };

    if (prevBtn) {
      prevBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        tabsNav.scrollBy({ left: -scrollAmount(), behavior: 'smooth' });
      });
    }
    if (nextBtn) {
      nextBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        tabsNav.scrollBy({ left: scrollAmount(), behavior: 'smooth' });
      });
    }

    tabsNav.addEventListener('scroll', updateButtons, { passive: true });
    window.addEventListener('resize', updateButtons);
    window.addEventListener('load', updateButtons);
    requestAnimationFrame(updateButtons);
  }

  onClick(event) {
    const tab = event.target.closest('[data-subheader-recipients-tab]');
    if (!tab) return;

    event.preventDefault();

    if (tab.classList.contains('is-active') || this.classList.contains('is-loading')) {
      return;
    }

    this.loadCollection(tab.dataset.tabUrl, { history: 'push' });
  }

  /**
   * Load a tab's collection into the grid.
   * options.history: 'push' (tab click), 'replace' (initial load, so the back
   * button isn't cluttered) or 'none'.
   */
  async loadCollection(url, options = {}) {
    const historyMode = options.history || 'push';

    if (!url || !this.sectionId) {
      window.location.href = url;
      return;
    }

    const gridWrapper = document.querySelector('.collection_grid');

    this.classList.add('is-loading');
    if (gridWrapper) gridWrapper.classList.add('is-loading');

    try {
      const separator = url.includes('?') ? '&' : '?';
      const fetchUrl = `${url}${separator}section_id=${this.sectionId}`;
      const response = await fetch(fetchUrl);

      if (!response.ok) {
        throw new Error(`Collection subheader recipients: request failed with status ${response.status}`);
      }

      const html = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');

      const newGrid = doc.querySelector('.collection_grid');
      const currentGrid = document.querySelector('.collection_grid');

      if (!newGrid || !currentGrid) {
        throw new Error('Collection subheader recipients: could not locate .collection_grid in response');
      }

      currentGrid.replaceWith(newGrid);

      const targetHandle = this.getHandleFromUrl(url);
      const activeTab = this.getTabs().find((tabEl) => this.getHandleFromUrl(tabEl.dataset.tabUrl) === targetHandle);
      if (activeTab) this.setActiveTab(activeTab);

      if (historyMode === 'push') {
        window.history.pushState({ collectionSubheaderUrl: url }, '', url);
      } else if (historyMode === 'replace') {
        window.history.replaceState({ collectionSubheaderUrl: url }, '', url);
      }

      document.dispatchEvent(
        new CustomEvent('collection-subheader-recipients:updated', { detail: { url } })
      );
    } catch (error) {
      console.error(error);
      window.location.href = url;
    } finally {
      this.classList.remove('is-loading');
      const refreshedGridWrapper = document.querySelector('.collection_grid');
      if (refreshedGridWrapper) refreshedGridWrapper.classList.remove('is-loading');
    }
  }
}

if (!customElements.get('collection-subheader-recipients')) {
  customElements.define('collection-subheader-recipients', CollectionSubheaderRecipients);
}

window.addEventListener('popstate', (event) => {
  if (event.state && event.state.collectionSubheaderUrl) {
    window.location.reload();
  }
});

/* =====================================================================
   Product grid reveal fix
   Paste this at the very END of collection-subheader.js (Shop All
   template) and collection-subheader-recipients.js (Recipients template).
   Nothing above it needs to change.

   Why: the theme hides product cards ([data-animate] => opacity .001)
   until its scroll-reveal script sees them. When a tab swaps in a new
   grid, the script doesn't notice the new cards until the page scrolls,
   so the grid looks empty.

   What it does, after a tab switch or page load:
   1. Nudges the theme's own scroll-reveal (keeps the normal fade-in).
   2. A moment later, force-shows any on-screen card that is still hidden.
   ===================================================================== */
(function () {
  if (window.__collectionGridRevealFix) return;
  window.__collectionGridRevealFix = true;

  function isOnScreen(el) {
    var rect = el.getBoundingClientRect();
    return rect.bottom > 0 && rect.top < window.innerHeight;
  }

  function hydrateImages(grid) {
    grid.querySelectorAll('img').forEach(function (img) {
      if (img.dataset.src && !img.getAttribute('src')) img.setAttribute('src', img.dataset.src);
      if (img.dataset.srcset && !img.getAttribute('srcset')) img.setAttribute('srcset', img.dataset.srcset);
    });
    grid.querySelectorAll('source[data-srcset]').forEach(function (source) {
      if (!source.getAttribute('srcset')) source.setAttribute('srcset', source.dataset.srcset);
    });
  }

  function revealHiddenOnScreen(grid) {
    grid.querySelectorAll('[data-animate]').forEach(function (el) {
      if (!isOnScreen(el)) return; // off-screen cards keep the theme's normal scroll reveal
      if (parseFloat(window.getComputedStyle(el).opacity) < 0.1) {
        el.style.opacity = '1';
        el.style.transform = 'none';
      }
    });
  }

  function refreshGrid() {
    var grid = document.querySelector('.collection_grid');
    if (!grid) return;

    hydrateImages(grid);

    // 1. Let the theme's own scroll-reveal run first
    window.dispatchEvent(new Event('scroll'));
    window.dispatchEvent(new Event('resize'));

    // 2. Safety net for anything still hidden
    setTimeout(function () { revealHiddenOnScreen(grid); }, 350);
  }

  // After a tab switch (Shop All and Recipients templates)
  document.addEventListener('collection-subheader:updated', refreshGrid);
  document.addEventListener('collection-subheader-recipients:updated', refreshGrid);

  // On first page load
  if (document.readyState === 'complete') {
    refreshGrid();
  } else {
    window.addEventListener('load', refreshGrid);
  }
})();
